import React, { useEffect, useState } from "react";
import { fireStore } from "../../firebaseConfig";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  query,
  where,
  Timestamp,
} from "firebase/firestore";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import "./Vehicles.css";
import { Link } from "react-router-dom";
import { BiEdit, BiEditAlt, BiRecycle } from "react-icons/bi";
import { FaDownload } from "react-icons/fa6";
import { TiMediaFastForward } from "react-icons/ti";

const Vehicles = () => {
  const now = new Date();
  const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1); // First day of the current month
  firstDayOfMonth.setUTCHours(0, 0, 0, 0); // Reset time to start of the day

  const [startDate, setStartDate] = useState(firstDayOfMonth.getTime()); // Store as milliseconds
  const [endDate, setEndDate] = useState(() => {
    const now = new Date();
    now.setUTCHours(23, 59, 59, 999); // Set to the last millisecond of the day
    return now.getTime(); // Store as milliseconds
  });
  // Store current time in milliseconds

  const [logbooks, setLogbooks] = useState([]);
  const [meterChanged, setMeterChanged] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [filteredVehicles, setFilteredVehicles] = useState([]);
  const [territoryQuery, setTerritoryQuery] = useState("");
  const [regNumSearch, setRegNumSearch] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [fuelAverage, setFuelAverage] = useState(0);
  const [meterReadingValues, setMeterReadingValues] = useState(
    filteredVehicles.map((vehicle) => vehicle.meterReading)
  );
  const [downloadVehicleDetails, setDownloadVehicleDetails] = useState(null);

  const [dowVisible, setDowVisible] = useState(false);

  // Fetch Vehicles...
  useEffect(() => {
    const fetchVehicles = async () => {
      try {
        const collectionRef = collection(fireStore, "Vehicles");
        const querySnapshot = await getDocs(collectionRef);

        const vehiclesList = querySnapshot.docs.map((doc) => ({
          ...doc.data(),
        }));
        const sortedVehiclesList = vehiclesList.sort((a, b) => {
          return b.year - a.year;
        });

        setVehicles(sortedVehiclesList);
        setFilteredVehicles(sortedVehiclesList);
      } catch (error) {
        console.error("Error fetching vehicles:", error);
      }
    };

    fetchVehicles();
  }, [meterChanged]);

  // Update Vehicles...
  const handleUpdateValue = async (index) => {
    try {
      const vehicleId = filteredVehicles[index].id;
      const updatedMeterReading = meterReadingValues[index];
      const vehicleDocRef = doc(fireStore, "Vehicles", vehicleId);
      await updateDoc(vehicleDocRef, { meterReading: updatedMeterReading });
      const updatedVehicles = [...vehicles];
      updatedVehicles[index].meterReading = updatedMeterReading;
      setVehicles(updatedVehicles);
      setIsEditing(null);
      setMeterChanged(!meterChanged);
    } catch (error) {
      console.error("Error updating meter reading:", error);
    }
  };

  // Edit A Vehicle...
  const handleEditClick = (index) => {
    const newMeterReadingValues = [...meterReadingValues];
    newMeterReadingValues[index] = filteredVehicles[index].meterReading;
    setMeterReadingValues(newMeterReadingValues);
    setIsEditing(isEditing === index ? null : index);
  };

  // Handle meter Reading Change...
  const handleMeterReadingChange = (index, event) => {
    const newValues = [...meterReadingValues];
    newValues[index] = parseInt(event.target.value, 10);
    setMeterReadingValues(newValues);
  };

  // Delete A Vehicle...
  const handleDelete = async (vehicleId, index) => {
    try {
      const vehicleDocRef = doc(fireStore, "Vehicles", vehicleId);
      await deleteDoc(vehicleDocRef);

      const updatedVehicles = vehicles.filter((_, i) => i !== index);
      setVehicles(updatedVehicles);
    } catch (error) {
      console.error("Error deleting vehicle:", error);
    }
  };

  // Confirm Delete
  const confirmDelete = (id, index) => {
    setSelectedVehicle({ id, index });
    setShowModal(true);
  };

  // Cancel Delete...
  const handleCancelDelete = () => {
    setShowModal(false);
    setSelectedVehicle(null);
  };

  // Confirm Delete..
  const handleConfirmDelete = () => {
    if (selectedVehicle) {
      handleDelete(selectedVehicle.id, selectedVehicle.index);
    }
    setShowModal(false);
    setSelectedVehicle(null);
  };

  useEffect(() => {
    const filtered = vehicles.filter((vehicle) => {
      const regNumber = vehicle.regNumber
        ? vehicle.regNumber.toLowerCase()
        : "";
      const userTerritory = vehicle.user.territory
        ? vehicle.user.territory.toLowerCase()
        : "";

      return (
        regNumber.toLowerCase().includes(regNumSearch.toLowerCase()) &&
        userTerritory.toLowerCase().includes(territoryQuery.toLowerCase())
      );
    });

    setFilteredVehicles(filtered);
  }, [regNumSearch, territoryQuery]);

  // Fetch LogBooks...
  const handleDownloadPDF = async (vehicle) => {
    try {
      const startTimestamp = Timestamp.fromDate(new Date(startDate));
      const endTimestamp = Timestamp.fromDate(new Date(endDate));

      const logbooksRef = collection(fireStore, "LogBooks");

      const q = query(
        logbooksRef,
        where("time", ">=", startTimestamp),
        where("time", "<=", endTimestamp)
      );

      const querySnapshot = await getDocs(q);
      const logbooksList = querySnapshot.docs.map((doc) => doc.data());

      const sortedLogbooksList = logbooksList.sort((a, b) => a.id - b.id);

      const filteredLogbooks = sortedLogbooksList.filter(
        (item) => item.vehicleNo === vehicle.regNumber
      );

      // Set the logbooks and vehicle details for download
      if (filteredLogbooks.length > 0) {
        setLogbooks(filteredLogbooks);
        setDownloadVehicleDetails(vehicle);
        setDowVisible(true);
      } else {
        alert("No LogBook Found!");
      }
    } catch (error) {
      console.error("Error downloading PDF:", error);
    }
  };

  useEffect(() => {
    if (!dowVisible) {
      downloadPDF();
    }
  }, [dowVisible]);

  // Download PDF
  async function downloadPDF() {
    const element = document.getElementById("logbook-section");
    if (!element) {
      return;
    }
    const canvas = await html2canvas(element, { scale: 1 });
    const imgData = canvas.toDataURL("image/jpeg", 0.7);

    const pdf = new jsPDF("p", "mm", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    let imgHeight = (canvas.height * pdfWidth) / canvas.width;
    let position = 0;

    while (imgHeight > 0) {
      const sliceHeight = Math.min(pageHeight, imgHeight);

      const canvasSlice = document.createElement("canvas");
      const ctx = canvasSlice.getContext("2d");
      canvasSlice.width = canvas.width;
      canvasSlice.height = (sliceHeight * canvas.width) / pdfWidth;

      ctx.drawImage(
        canvas,
        0,
        (position * canvas.width) / pdfWidth,
        canvas.width,
        (sliceHeight * canvas.width) / pdfWidth,
        0,
        0,
        canvasSlice.width,
        canvasSlice.height
      );

      const imgSliceData = canvasSlice.toDataURL("image/jpeg",0.7);

      if (position === 0) {
        pdf.addImage(imgSliceData, "PNG", 0, 0, pdfWidth, sliceHeight);
      } else {
        pdf.addPage();
        pdf.addImage(imgSliceData, "PNG", 0, 0, pdfWidth, sliceHeight);
      }

      imgHeight -= sliceHeight;
      position += sliceHeight;
    }

    pdf.save(`logbook_${downloadVehicleDetails.regNumber}.pdf`);

    // Reset states
    setDownloadVehicleDetails(null);
  }

  useEffect(() => {
    const totalKms = logbooks.reduce((total, logbook) => {
      const driveKms = Number(logbook.driveKms);
      return !isNaN(driveKms) ? total + driveKms : total;
    }, 0);

    const totalFuel = logbooks.reduce((total, logbook) => {
      const fuelQuantity = Number(logbook.fuelQuantity);
      return !isNaN(fuelQuantity) ? total + fuelQuantity : total;
    }, 0);

    // Ensure no division by zero
    setFuelAverage(totalFuel === 0 ? 0 : +(totalKms / totalFuel).toFixed(2));
  }, [logbooks]);

  return (
    <div className="p-4 bg-lightgray h-dvh overflow-y-scroll">
      {/* inside the main page... */}

      <div className="max-w-full mx-auto mt-4  bg-white p-6 rounded-lg shadow-lg">
        {/* this is innder white section */}

        <div className="flex flex-col md:flex-row justify-between items-center">
          <h2 className="text-2xl font-semibold mb-4 md:mb-0 font-lato">
            Vehicles ({vehicles.length})
          </h2>
        </div>
        <div className="py-2 flex items-center justify-between">
          <div>
            <input
              type="text"
              placeholder="Search Territory"
              className="w-full md:w-auto border border-gray-300 px-5 py-1.5 focus:ring focus:ring-blue-300"
              value={territoryQuery}
              onChange={(e) => setTerritoryQuery(e.target.value)}
            />
          </div>
          <div className="relative">
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              className="absolute left-3 top-1/2 transform -translate-y-1/2 "
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6.5 0C8.22391 0 9.87721 0.684819 11.0962 1.90381C12.3152 3.12279 13 4.77609 13 6.5C13 8.11 12.41 9.59 11.44 10.73L11.71 11H12.5L17.5 16L16 17.5L11 12.5V11.71L10.73 11.44C9.59 12.41 8.11 13 6.5 13C4.77609 13 3.12279 12.3152 1.90381 11.0962C0.684819 9.87721 0 8.22391 0 6.5C0 4.77609 0.684819 3.12279 1.90381 1.90381C3.12279 0.684819 4.77609 0 6.5 0ZM6.5 2C4 2 2 4 2 6.5C2 9 4 11 6.5 11C9 11 11 9 11 6.5C11 4 9 2 6.5 2Z"
                fill="#056886"
              />
            </svg>
            <input
              type="text"
              placeholder="MN5503"
              className="w-full md:w-auto border border-gray-300 rounded-full px-10 py-2 focus:ring focus:ring-blue-300"
              value={regNumSearch}
              onChange={(e) => setRegNumSearch(e.target.value)}
            />
          </div>
          <div>
            <Link to="/addVehicle">
              <button className="text-darkteal font-semibold text-xl">
                + Add new
              </button>
            </Link>
          </div>
        </div>
        <input
          className="mr-6"
          type="date"
          value={new Date(startDate).toISOString().split("T")[0]} // Convert timestamp to YYYY-MM-DD format
          onChange={(e) => {
            const selectedDate = new Date(e.target.value);
            selectedDate.setUTCHours(0, 0, 0, 0); // Set time to 00:00 UTC
            setStartDate(selectedDate.getTime());
          }}
        ></input>
        <input
          type="date"
          value={new Date(endDate).toISOString().split("T")[0]} // Convert timestamp to YYYY-MM-DD format
          onChange={(e) => {
            const selectedDate = new Date(e.target.value);
            selectedDate.setUTCHours(23, 59, 59, 999); // Set to the last millisecond of the day
            setEndDate(selectedDate.getTime()); // Store as timestamp
          }}
        ></input>
        {/*Cards*/}
        <div className="bg-white p-4 mb-4 h-[450px] overflow-y-scroll">
          {filteredVehicles.map((vehicle, index) => (
            <div key={vehicle.id}>
              <div
                key={index}
                className="flex flex-col p-4  mb-4 bg-white rounded-lg shadow-lg border border-gray-200"
              >
                <div className="grid grid-cols-5">
                  <div>
                    <h3 className="text-lg font-semibold  mb-4">
                      {vehicle.name}
                    </h3>
                    <span className="bg-white border border-gray-400 px-5 py-1 shadow-lg rounded-md text-darkteal text-lg font-semibold">
                      {vehicle.regNumber}
                    </span>
                  </div>
                  <div className="text-center">
                    {/*<p className="text-lg font-semibold mb-3">{vehicle.meterReading}</p>*/}
                    {isEditing === index ? (
                      <input
                        type="text"
                        value={meterReadingValues[index]}
                        onChange={(event) =>
                          handleMeterReadingChange(index, event)
                        }
                        className="border border-gray-300 px-2 py-1 rounded-md text-lg font-semibold"
                      />
                    ) : (
                      <p className="text-lg font-semibold mb-3">
                        {vehicle.meterReading}
                      </p>
                    )}
                    <p
                      style={{ paddingLeft: "4rem" }}
                      className="text-gray-600 text-lg font-semibold"
                    >
                      {vehicle.year}
                    </p>
                  </div>
                  <div className="text-end relative pe-6 space-y-1">
                    <div className="absolute right-0">
                      <TiMediaFastForward
                        className=" cursor-pointer"
                        onClick={() => handleEditClick(index)}
                      />
                    </div>
                    <p className="text-darkteal font-semibold">
                      {vehicle.color}
                    </p>
                    <p className="text-sm text-gray-500">{vehicle.engine}</p>
                    <p className="text-sm text-gray-500 mb-2">{vehicle.make}</p>
                    {isEditing === index && (
                      <div className="mr-12">
                        <button
                          onClick={() => handleUpdateValue(index)} // Wrap in arrow function
                          className="bg-darkteal text-white px-4 py-2 rounded-md"
                        >
                          Update
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="col-span-2 border-l-2 border-l-darkteal ms-4 ps-4 relative space-y-1">
                    <div
                      className="absolute right-16 cursor-pointer"
                      onClick={() => handleDownloadPDF(vehicle)}
                    >
                      <FaDownload />
                    </div>
                    <div className="absolute right-8 cursor-pointer">
                      <Link to={`/updateVehicle/${vehicle.id}`}>
                        <BiEdit />
                      </Link>
                    </div>
                    <div
                      className="absolute right-0 cursor-pointer"
                      onClick={() => confirmDelete(vehicle.id, index)}
                    >
                      <BiRecycle />
                    </div>
                    <p className="font-medium">{vehicle.user.name}</p>
                    <p className="text-blue-500 font-semibold">
                      {vehicle.user.role}
                    </p>
                    <p className="text-sm text-gray-500 text-end">
                      {vehicle.user.territory}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Modal */}
          {showModal && (
            <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50">
              <div className="bg-white p-6 rounded-lg shadow-lg">
                <h2 className="text-lg font-semibold mb-4">
                  Are you sure you want to delete?
                </h2>
                <div className="flex justify-end">
                  <button
                    className="px-4 py-2 bg-red-500 text-white rounded mr-2"
                    onClick={handleConfirmDelete}
                  >
                    Yes
                  </button>
                  <button
                    className="px-4 py-2 bg-gray-300 rounded"
                    onClick={handleCancelDelete}
                  >
                    No
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white mt-4 shadow-sm shadow-gray-500">
        {/* PDF PAGE */}
        {downloadVehicleDetails !== null && (
          <div id="logbook-section" className="p-12 h-fit flex flex-col ">
            {dowVisible && (
              <p
                onClick={() => {
                  setDowVisible(false);
                  // downloadPDF();
                }}
                className="font-bold bg-teal-800 p-2 text-gray-400 w-fit rounded-md self-end hover:bg-teal-500 hover:text-white"
                role="button"
              >
                Download
              </p>
            )}
            <h1 className="text-2xl font-bold">Log Book</h1>
            <div className="grid grid-cols-4 gap-4 mb-4">
              <div>
                <h3 className="font-semibold">Month:</h3>
                {}
                <p>
                  {
                    // Convert Firebase Timestamp to a JavaScript Date object
                    (logbooks?.[0].time &&
                      logbooks?.[0].time
                        .toDate()
                        .toLocaleDateString("default", { month: "long" })) ||
                      "" ||
                      ""
                  }
                </p>
              </div>
              <div>
                <h3 className="font-semibold">ABM/ZBM/HSM/CEO Name:</h3>
                <p>{downloadVehicleDetails.user.name}</p>
              </div>
              <div>
                <h3 className="font-semibold">Vehicle:</h3>
                <p>{downloadVehicleDetails.regNumber}</p>
              </div>
              <div>
                <h3 className="font-semibold">FuelAverage:</h3>
                <p className="font-bold text-lg">{fuelAverage}</p>
              </div>
              <div>
                <h3 className="font-semibold">Territory:</h3>
                <p>{logbooks?.[0].user.territory.name}</p>
              </div>
              <div>
                <h3 className="font-semibold">Model:</h3>
                <p>{downloadVehicleDetails.name}</p>
              </div>
              <div>
                <h3 className="font-semibold">Engine:</h3>
                <p>{downloadVehicleDetails.engine}</p>
              </div>
            </div>

            {/* Table Section */}
            <table className="table-auto w-full border-collapse border border-gray-300 text-sm">
              <thead className="bg-teal-400">
                <tr>
                  <th className="border border-gray-300 p-2">Date</th>
                  <th className="border border-gray-300 p-2" colSpan="2">
                    Meter Reading
                  </th>
                  <th className="border border-gray-300 p-2" rowSpan="2">
                    Travelled KM
                  </th>
                  <th className="border border-gray-300 p-2" rowSpan="2">
                    Purpose of Travel (Area Visited)
                  </th>
                  <th className="border border-gray-300 p-2" rowSpan="2">
                    Fuel Qty (Ltr)
                  </th>
                  <th className="border border-gray-300 p-2" rowSpan="2">
                    Fuel Amount (Rs.)
                  </th>
                  <th className="border border-gray-300 p-2" rowSpan="2">
                    Remarks
                  </th>
                </tr>
                <tr>
                  <th></th>
                  <th>Open</th>
                  <th>Closed</th>
                </tr>
              </thead>
              <tbody>
                {logbooks.map((logbook) => (
                  <tr key={logbook.id} className="text-center">
                    <td className="border border-gray-300 p-2">
                      {logbook.time.toDate().toLocaleDateString("en-US")}
                    </td>
                    <td className="border border-gray-400 px-2 py-1">
                      {logbook.openMeterReading}
                    </td>
                    <td className="border border-gray-400 px-2 py-1">
                      {logbook.closeMeterReading}
                    </td>
                    <td className="border border-gray-300 p-2 ">
                      {logbook.driveKms || ""}
                    </td>
                    <td className="border border-gray-300 p-2">
                      {logbook.areaVisited || ""}
                    </td>
                    <td className="border border-gray-300 p-2">
                      {logbook.fuelQuantity > 0
                        ? logbook.fuelQuantity.toFixed(2)
                        : ""}
                    </td>
                    <td className="border border-gray-300 p-2">
                      {logbook.fuelPrice > 0
                        ? logbook.fuelPrice.toFixed(2)
                        : ""}
                    </td>
                    <td className="border border-gray-300 p-2">
                      {logbook.remarks || ""}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="content-center items-center text-center">
                <tr className="font-semibold bg-teal-200">
                  <td
                    className="border border-gray-300 p-2 text-center"
                    colSpan="2"
                  >
                    TOTALS
                  </td>
                  <td className="border border-gray-300 p-2 text-center"></td>
                  <td className="border border-gray-300 p-2 text-center">
                    {logbooks.reduce(
                      (total, logbook) => total + (logbook.driveKms || 0),
                      0
                    )}
                  </td>
                  <td className="border border-gray-300 p-2 text-center"></td>
                  <td className="border border-gray-300 p-2 text-center">
                    {logbooks
                      .reduce(
                        (total, logbook) => total + (logbook.fuelQuantity || 0),
                        0
                      )
                      .toFixed(2)}
                  </td>
                  <td className="border border-gray-300 p-2 text-center">
                    {logbooks.reduce(
                      (total, logbook) => total + (logbook.fuelPrice || 0),
                      0
                    )}
                  </td>
                  <td className="border border-gray-300 p-2 text-center"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Vehicles;
